import React, { useEffect } from "react";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
  useNavigate,
  useParams,
} from "react-router-dom";

import LoginView from "../views/LoginView/LoginView";
import { Typography } from "@mui/material";
import RegisterView from "../views/RegisterView/RegisterView";
import ManagerLoginView from "../views/ManagerLoginView/ManagerLoginView";
import UserPasswordResetView from "../views/UserPasswordResetView/UserPasswordResetView";
import ManagerPasswordResetView from "../views/ManagerPasswordResetView/ManagerPasswordResetView";
import CaseProgressView from "../views/CaseProgressView/CaseProgressView";
import NotFoundView from "../views/NotFoundView/NotFoundView";
import IntakeView from "../views/IntakeView/IntakeView";
import IntakeReturnView from "../views/IntakeReturnView/IntakeReturnView";
import FinishUserAccountView from "../views/FinishUserAccountView/FinishUserAccountView";
import VerifyUserView from "../views/VerifyUserView/VerifyUserView";

const Redir = (props: { to: string }) => {
  const navigate = useNavigate();

  useEffect(() => {
    navigate(props.to);
  });

  return (
    <>
      <Typography variant="h3">Redirecting...</Typography>
    </>
  );
};

/**
 * Ken's Aug 5 button map spells the non-CDL plan pages `/noncdl/:tier`; the app has always served
 * `/non-cdl/:tier`, which is the path in the intake design spec, the EOD reports and the Google
 * Analytics commitment made to Lloyd. The mismatch would not have shown up until the moment the six
 * buttons went live, because the holding page answers 200 for every path on that subdomain — a dead
 * link that looks like a working one.
 *
 * A redirect rather than a second route pointing at the same view: two live paths for one page would
 * split the GA path report, which is the entire reason the plan is in the URL, and would leave
 * "which spelling is canonical" for someone to get wrong later. Either map now works and only one
 * path is ever reported.
 */
const NonCdlAliasRedirect = () => {
  const { tier } = useParams();
  return <Navigate to={`/non-cdl/${tier ?? ""}`} replace />;
};

const DefaultRouter = createBrowserRouter(
  [
    {
      path: "/",
      element: <LoginView />,
    },
    {
      path: "/manager-login",
      element: <ManagerLoginView />,
    },
    {
      path: "/login",
      element: <LoginView />,
    },
    {
      path: "/register",
      element: <RegisterView />,
    },
    {
      path: "/reset-password",
      element: <UserPasswordResetView />,
    },
    {
      path: "/manager-reset-password",
      element: <ManagerPasswordResetView />,
    },
    {
      // Partner case link. Registered in both routers so it opens the same whether or not the
      // visitor happens to have a staff session — a referral partner has no login at all.
      path: "/case/:token",
      element: <CaseProgressView />,
    },
    {
      // Public intake. The path carries the plan and nothing else — no personal data reaches a URL
      // an analytics script can read. These are the six buttons Ken repoints on the advertising
      // site, e.g. /non-cdl/best.
      //
      // Listed per product rather than as /:product/:tier, which would match any two-segment path
      // in the app and answer for routes that do not exist.
      path: "/non-cdl/:tier",
      element: <IntakeView product="non-cdl" />,
    },
    {
      // Ken's spelling. Redirects onto the canonical path above — see NonCdlAliasRedirect.
      path: "/noncdl/:tier",
      element: <NonCdlAliasRedirect />,
    },
    {
      path: "/cdl/:tier",
      element: <IntakeView product="cdl" />,
    },
    {
      // Where Stripe returns a client after paying. Both paths are named by the backend's
      // /public/intake/complete route; until these existed they fell through to "*" and a client who
      // had just paid was shown Not Found.
      path: "/thank-you",
      element: <IntakeReturnView outcome="paid" />,
    },
    {
      // Paid, but the case did not get created. Same reassurance, no blame, and staff already hold
      // the alert — see IntakeReturnView.
      path: "/received",
      element: <IntakeReturnView outcome="received" />,
    },
    {
      path: "/finish-account-creation",
      element: <FinishUserAccountView />,
    },
    {
      path: "/verify-account",
      element: <VerifyUserView />,
    },
    {
      path: "*",
      element: <NotFoundView />,
    },
  ],
  {}
);
export default DefaultRouter;